/* MK theme — workflow demo (motion designer).
 *
 * Self-initialising on [data-mk-demo].
 *
 * Tabs: scenario switch (design-system §10, --mk-dur-scene 400ms) — the current panel fades out, the
 * next one shows with its cards at 0 and its route undrawn, then cards arrive 60ms apart along the route
 * while the route draws and the nodes appear in sequence (`.is-drawn`, the same mechanism reveal.js
 * uses). Arrow keys are direction-aware; Home/End; selection follows focus. Remembers nothing.
 *
 * Auto-run (engagement pass §3.2): when the panel is ≥ 60% visible, the visitor has not interacted with
 * it this session, and motion is allowed, the current scenario plays once: each step lights after
 * --mk-dur-autoplay (its card settles from .4 to 1, the segment to it draws, its node fills; the human
 * step gets its box outline when it lights), the counter line counts as steps light, then it stops.
 * Any click / key / touch on the panel stops auto-run for the session (sessionStorage). Offscreen pauses
 * it. The Play control replays the current scenario on demand (and pauses it).
 *
 * Routes outside the demo and outside any [data-mk-reveal] section (e.g. the service-page intake
 * scene) draw once when 40% visible, so they never depend on reveal.js.
 */

const GUARD = '__mkWorkflowDemo';
if (!window[GUARD]) {
  window[GUARD] = true;

  const REDUCE = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const AUTO_KEY = 'mk-demo-interacted';

  function cssMs(el, name, fallback) {
    const raw = getComputedStyle(el).getPropertyValue(name).trim();
    if (!raw) return fallback;
    return raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
  }
  const storage = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  function init(root) {
    if (root.dataset.mkDemoReady) return;
    root.dataset.mkDemoReady = '1';

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls'))).filter(Boolean);
    if (tabs.length < 2 || panels.length !== tabs.length) return;
    const reduce = () => REDUCE && REDUCE.matches;

    let current = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
    let switching = 0;

    panels.forEach((p, i) => {
      p.hidden = i !== current;
    });

    // ------------------------------------------------------------------ counter
    const counterFor = (panel) => panel.querySelector('[data-mk-demo-counter]');
    const setCounter = (panel, sys, person) => {
      const c = counterFor(panel);
      if (!c || !c.dataset.sysMany) return;
      const d = c.dataset;
      const one = (n, single, many) => (n === 1 ? single : many).replace('%s', String(n));
      // The human part appears once a person has decided (never "0 decisions" mid-run).
      const parts = [one(sys, d.sysOne, d.sysMany)];
      if (person > 0) parts.push(one(person, d.personOne, d.personMany));
      c.textContent = parts.join(d.sep || ' · ');
    };
    const countLit = (panel, litOnly) => {
      const steps = Array.from(panel.querySelectorAll('.mk-demo__step'));
      let sys = 0;
      let person = 0;
      steps.forEach((li) => {
        if (litOnly && !li.classList.contains('is-lit')) return;
        if (li.classList.contains('mk-demo__step--human')) person++;
        else if (li.classList.contains('mk-demo__step--automation')) sys++;
      });
      return [sys, person];
    };

    // ------------------------------------------------------------------ route draw
    const drawRoute = (panel, animate) => {
      const route = panel.querySelector('[data-mk-route]');
      if (!route) return;
      route.classList.remove('is-autoplay');
      if (!animate) {
        route.classList.add('is-drawn');
        return;
      }
      route.classList.remove('is-drawn');
      route.classList.add('is-redrawing');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        route.classList.remove('is-redrawing');
        route.classList.add('is-drawn');
      }));
    };

    // ------------------------------------------------------------------ auto-run
    const playBtn = root.querySelector('[data-mk-demo-play]');
    let auto = { state: 'idle', timer: 0, panel: null, steps: [], next: 0, manual: false };
    let interacted = storage.get(AUTO_KEY) === '1';
    let inView = false;

    const setPlayLabel = (playing) => {
      if (!playBtn) return;
      playBtn.textContent = playing ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay;
      playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    };

    const finishAuto = () => {
      clearTimeout(auto.timer);
      const { panel } = auto;
      if (panel) {
        const route = panel.querySelector('[data-mk-route]');
        panel.querySelectorAll('.mk-demo__step').forEach((li) => li.classList.add('is-lit', 'is-lit-out'));
        if (route) {
          route.classList.remove('is-autoplay');
          route.classList.add('is-drawn');
        }
        const [s, p] = countLit(panel, false);
        setCounter(panel, s, p);
      }
      auto = { state: 'done', timer: 0, panel: null, steps: [], next: 0, manual: false };
      setPlayLabel(false);
    };

    const lightNext = () => {
      const { panel, steps } = auto;
      const i = auto.next;
      if (i >= steps.length) {
        // Rest a moment on the completed frame, then hand the panel back to its normal state.
        auto.timer = setTimeout(finishAuto, 600);
        return;
      }
      steps[i].classList.add('is-lit');
      if (i > 0) steps[i - 1].classList.add('is-lit-out');
      const [s, p] = countLit(panel, true);
      setCounter(panel, s, p);
      auto.next = i + 1;
      auto.timer = setTimeout(lightNext, cssMs(root, '--mk-dur-autoplay', 1800));
    };

    const startAuto = (manual) => {
      if (auto.state === 'playing') return;
      const panel = panels[current];
      const route = panel.querySelector('[data-mk-route]');
      const steps = Array.from(panel.querySelectorAll('.mk-demo__step'));
      if (!route || !steps.length) return;
      clearTimeout(auto.timer);
      steps.forEach((li) => li.classList.remove('is-lit', 'is-lit-out'));
      route.classList.remove('is-drawn', 'is-redrawing');
      route.classList.add('is-autoplay');
      setCounter(panel, 0, 0);
      auto = { state: 'playing', timer: 0, panel, steps, next: 0, manual: !!manual };
      setPlayLabel(true);
      // First step lights right away.
      auto.timer = setTimeout(lightNext, 250);
    };

    const pauseAuto = () => {
      if (auto.state !== 'playing') return;
      clearTimeout(auto.timer);
      auto.state = 'paused';
      setPlayLabel(false);
    };
    const resumeAuto = () => {
      if (auto.state !== 'paused') return;
      auto.state = 'playing';
      setPlayLabel(true);
      auto.timer = setTimeout(lightNext, 400);
    };

    // Any interaction with the panel ends the unattended auto-run for this session.
    const markInteracted = () => {
      if (!interacted) {
        interacted = true;
        storage.set(AUTO_KEY, '1');
      }
      if ((auto.state === 'playing' || auto.state === 'paused') && !auto.manual) finishAuto();
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((ev) => {
      root.addEventListener(ev, (e) => {
        if (playBtn && (e.target === playBtn || playBtn.contains(e.target))) return;
        markInteracted();
      }, { passive: true, capture: true });
    });

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        interacted = true;
        storage.set(AUTO_KEY, '1');
        if (auto.state === 'playing') pauseAuto();
        else if (auto.state === 'paused') resumeAuto();
        else startAuto(true);
      });
      if (reduce()) playBtn.hidden = true;
    }

    // ------------------------------------------------------------------ tabs
    const setTabs = (next) => {
      tabs.forEach((tab, i) => {
        const on = i === next;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    };

    const select = (next, focus) => {
      if (next === current || next < 0 || next >= tabs.length) {
        if (focus) tabs[next]?.focus();
        return;
      }
      if (auto.state === 'playing' || auto.state === 'paused') finishAuto();
      const from = panels[current];
      const to = panels[next];
      const instant = reduce();
      const half = instant ? 0 : cssMs(root, '--mk-dur-scene', 400) / 2;
      const token = ++switching;

      current = next;
      setTabs(next);
      if (focus) tabs[next].focus();

      const show = () => {
        if (token !== switching) return;
        from.hidden = true;
        from.classList.remove('is-leaving');
        to.classList.add('is-entering');
        to.hidden = false;
        const [s, p] = countLit(to, false);
        setCounter(to, s, p);
        if (instant) {
          to.classList.remove('is-entering');
          drawRoute(to, false);
          return;
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== switching) return;
            to.classList.remove('is-entering');
            drawRoute(to, true);
          });
        });
      };

      if (instant) show();
      else {
        from.classList.add('is-leaving');
        setTimeout(show, half);
      }
    };

    // Scroll hint on the (mobile) scrolling tab row.
    const list = root.querySelector('[role="tablist"]');
    if (list) {
      const hint = () => {
        const more = list.scrollWidth - list.clientWidth - Math.abs(list.scrollLeft) > 4;
        if (more) list.setAttribute('data-scroll', 'more');
        else list.removeAttribute('data-scroll');
      };
      list.addEventListener('scroll', hint, { passive: true });
      window.addEventListener('resize', hint, { passive: true });
      hint();
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false));
      tab.addEventListener('keydown', (e) => {
        const rtl = getComputedStyle(root).direction === 'rtl';
        const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = rtl ? 'ArrowRight' : 'ArrowLeft';
        let next = null;
        if (e.key === forward || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
        else if (e.key === back || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        if (next === i) return;
        select(next, true);
      });
    });

    // ------------------------------------------------------------------ visibility
    // Initial counter = totals (true by construction). The first panel's route: auto-run lights it if
    // allowed; otherwise it draws once when 40% visible.
    panels.forEach((p) => {
      const [s, pp] = countLit(p, false);
      setCounter(p, s, pp);
    });

    const first = panels[current].querySelector('[data-mk-route]');
    if (reduce() || !('IntersectionObserver' in window)) {
      if (first) first.classList.add('is-drawn');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            inView = e.isIntersecting && e.intersectionRatio >= 0.6;
            const somewhat = e.isIntersecting && e.intersectionRatio >= 0.4;
            if (inView && !interacted && auto.state === 'idle') startAuto(false);
            else if (inView && auto.state === 'paused') resumeAuto();
            else if (!e.isIntersecting && auto.state === 'playing') pauseAuto();
            if (somewhat && auto.state === 'idle' && first && !first.classList.contains('is-drawn') && interacted) {
              first.classList.add('is-drawn');
            }
          }
        },
        { threshold: [0, 0.4, 0.6, 0.8] }
      );
      io.observe(root);
    }
  }

  function drawLooseRoutes() {
    const noHover = window.matchMedia ? window.matchMedia('(hover: none), (max-width: 1023px)').matches : false;
    const loose = Array.from(document.querySelectorAll('[data-mk-route]')).filter(
      (el) => !el.closest('[data-mk-demo]') && !el.classList.contains('is-drawn')
        && (el.classList.contains('mk-scene--mini') ? noHover : !el.closest('[data-mk-reveal]'))
    );
    if (!loose.length) return;
    if (!('IntersectionObserver' in window) || (REDUCE && REDUCE.matches)) {
      loose.forEach((el) => el.classList.add('is-drawn'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-drawn');
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.4 }
    );
    loose.forEach((el) => io.observe(el));
  }

  const start = () => {
    document.querySelectorAll('[data-mk-demo]').forEach(init);
    drawLooseRoutes();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}

export {};
